import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { WorkspaceContext } from '@/features/Workspaces/state/WorkspaceContext';
import { WorkspaceService, CreateWorkspaceDTO, UpdateWorkspaceDTO } from "@/features/Workspaces/services/workspaceService";

/**
 * Hook para acessar o contexto de produto (produto ativo/selecionado)
 */
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

/**
 * Hook para obter todos os produtos
 * @param organizationId ID opcional da organização para filtrar
 */
export const useProducts = (organizationId?: string) => {
  return useQuery({
    queryKey: ['products', organizationId], 
    queryFn: () => WorkspaceService.getAll(organizationId),
    staleTime: 0, // Sempre considerar os dados obsoletos
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};

/**
 * Hook para obter produto por ID
 * @param workspaceId ID do produto
 */
export const useProductById = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: ['workspace', workspaceId], 
    queryFn: () => workspaceId ? WorkspaceService.getById(workspaceId) : null,
    staleTime: 0, // Sempre considerar os dados obsoletos
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    enabled: !!workspaceId
  });
};

/**
 * Hook para obter todos os produtos com contagem de usuários
 * @param organizationId ID opcional da organização para filtrar
 * @param options Opções adicionais para a query
 */
export const useProductsWithUserCount = (
  organizationId?: string,
       // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: Omit<UseQueryOptions<any[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['products-with-user-count', organizationId], 
    queryFn: () => WorkspaceService.getAllWithUserCount(organizationId),
    staleTime: 0, // Sempre considerar os dados obsoletos
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    ...options
  });
};

/**
 * Hook para criar produto
 */
export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (workspaceData: CreateWorkspaceDTO) => {
      console.log('[useCreateProduct] Iniciando criação de produto');
      const result = await WorkspaceService.create(workspaceData);
      console.log('[useCreateProduct] Produto criado com sucesso:', result);
      return result;
    },
    onSuccess: () => {
      console.log('[useCreateProduct] Invalidando queries de produtos');
      // Invalidar queries para atualizar lista de produtos e navbar
      queryClient.invalidateQueries({ 
        queryKey: ['current-organization-products'],
        exact: true
      });
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey[0] === 'user-products'
      });
    }
  });
};

/**
 * Hook para atualizar produto
 */
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string, data: UpdateWorkspaceDTO }) => {
      console.log('[useUpdateProduct] Atualizando produto:', id);
      const result = await WorkspaceService.update(id, data);
      console.log('[useUpdateProduct] Produto atualizado com sucesso:', result);
      return result;
    },
    onSuccess: (_data, variables) => {
      console.log('[useUpdateProduct] Invalidando queries após atualização');
      
      // Invalidar queries específicas
      queryClient.invalidateQueries({ 
        queryKey: ['current-organization-products'],
        exact: true
      });
      queryClient.invalidateQueries({ 
        queryKey: ['workspace', variables.id],
        exact: true
      });
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey[0] === 'user-products'
      });
    }
  });
};

/**
 * Hook para excluir produto
 */
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (workspaceId: string) => {
      console.log(`[useDeleteProduct] Iniciando exclusão do produto ${workspaceId}`);
      const result = await WorkspaceService.delete(workspaceId);
      
      if (!result.success) {
        console.error(`[useDeleteProduct] Falha na exclusão:`, result);
        throw new Error(result.message || 'Falha ao excluir produto');
      }
      
      // Log de detalhes para debug
      if (result.details) {
        console.log(`[useDeleteProduct] Detalhes da exclusão:`, result.details);
      }
      
      return result;
    },
    onSuccess: () => {
      console.log(`[useDeleteProduct] Exclusão bem-sucedida, invalidando queries`);
      
      // Invalidar queries para atualizar lista de produtos e navbar
      queryClient.invalidateQueries({ 
        queryKey: ['current-organization-products'],
        exact: true
      });
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey[0] === 'user-products'
      });
    }
  });
};
